import React, { useState, useRef, useEffect } from 'react';

const SignatureCanvas = ({ onSignatureChange, signature }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef({ drawing: false, x: 0, y: 0 });

  // initialize & handle resizing/hi-DPI
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    // Set canvas size to match CSS size
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    
    // Scale the context to match device pixel ratio
    ctx.scale(dpr, dpr);
    
    // Set canvas CSS size
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    
    // fill white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Set drawing properties
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    
    // if you have saved data, restore it
    if (signature) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = signature;
    }
  }, [signature]);

  // unified coordinate extractor
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  // pointer‐down
  const start = (e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    
    drawingRef.current = { drawing: true, x, y };
    
    // Start a new path
    ctx.beginPath();
    ctx.moveTo(x, y);
    
    // Draw a small dot for single clicks/taps
    ctx.beginPath();
    ctx.arc(x, y, 0.5, 0, Math.PI * 2);
    ctx.fill();
  };

  // pointer‐move
  const move = (e) => {
    if (!drawingRef.current.drawing) return;
    e.preventDefault();
    
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    
    // Draw line from last position to current position
    ctx.beginPath();
    ctx.moveTo(drawingRef.current.x, drawingRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    
    // Update last position
    drawingRef.current.x = x;
    drawingRef.current.y = y;
  };

  // pointer‐up / leave
  const stop = (e) => {
    if (drawingRef.current.drawing) {
      drawingRef.current.drawing = false;
      // Only call onSignatureChange when we finish drawing
      onSignatureChange?.(canvasRef.current.toDataURL());
    }
  };

  // clear
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Clear and refill with white background
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    onSignatureChange?.('');
  };

  return (
    <div className="signature-container">
      <canvas
        ref={canvasRef}
        className="w-full h-24 border border-gray-300 bg-white cursor-crosshair touch-none select-none"
        style={{ touchAction: 'none' }}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
      />
      <button
        type="button"
        onClick={clear}
        className="mt-2 px-3 py-1 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 border border-gray-300 transition"
      >
        Unterschrift löschen
      </button>
    </div>
  );
};

const InputField = ({ label, name, value, onChange, type = "text", placeholder, ...props }) => {
  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
        {...props}
      />
    </div>
  );
};

const QualityNoteCard = ({ onQualityDataChange, initialData = {} }) => {
  const [qualityData, setQualityData] = useState({
    datum: initialData.datum || '',
    werkstucke: initialData.werkstucke || '',
    signature: initialData.signature || '',
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...qualityData,
      [name]: value
    };
    setQualityData(updatedData);
    if (onQualityDataChange) {
      onQualityDataChange(updatedData);
    }
  };

  const handleSignatureChange = (signatureData) => {
    const updatedData = {
      ...qualityData,
      signature: signatureData
    };
    setQualityData(updatedData);
    if (onQualityDataChange) {
      onQualityDataChange(updatedData);
    }
  };

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const setCurrentDate = () => {
    const currentDate = getCurrentDate();
    const updatedData = {
      ...qualityData,
      datum: currentDate
    };
    setQualityData(updatedData);
    if (onQualityDataChange) {
      onQualityDataChange(updatedData);
    }
  };

  const isComplete = qualityData.datum && qualityData.werkstucke && qualityData.signature;

  return (
    <div className="bg-white shadow-lg rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Qualitätsprüfung
            </h3>
            <p className="text-blue-100 text-sm mt-1">
              Maße/Entgraten/Auszug usw. sind am gefertigten Objekt geprüft
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-white hover:text-blue-100 transition-colors duration-150"
            >
              <svg
                className={`w-6 h-6 transform transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-none' : 'max-h-0'} overflow-hidden`}>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date Field */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Datum:
                </label>
                <button
                  type="button"
                  onClick={setCurrentDate}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Heute setzen
                </button>
              </div>
              <input
                type="date"
                name="datum"
                value={qualityData.datum}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              />
            </div>

            {/* Number of Workpieces */}
            <InputField
              label="Anzahl der Werkstücke:"
              name="werkstucke"
              type="number"
              min="1"
              value={qualityData.werkstucke}
              onChange={handleInputChange}
              placeholder="Werkstücke"
            />
          </div>

          {/* Signature Section */}
          <div className="border-t pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Unterschrift des Prüfers
            </label>
            <div className="bg-gray-50 p-4 rounded-sm border">
              <SignatureCanvas 
                onSignatureChange={handleSignatureChange}
                signature={qualityData.signature}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Compact view when collapsed */}
      {!isExpanded && qualityData.datum && (
        <div className="px-6 py-3 bg-gray-50 border-t">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {new Date(qualityData.datum).toLocaleDateString('de-DE')}
              {qualityData.werkstucke && ` • ${qualityData.werkstucke} Werkstücke`}
            </span>
            <span className={`font-medium ${isComplete ? 'text-green-600' : 'text-yellow-600'}`}>
              {isComplete ? 'Vollständig' : 'Unvollständig'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualityNoteCard;